import * as React from 'react';
import moment from 'moment-timezone';
import { FORMATS } from '@lib/common';
import { inject, observer } from 'mobx-react';
import { Button, DropDown, Modal, ModalContent, ModalTitle, RotateLoader } from '@lib/components';
import { FaCheck, FaChevronDown } from 'react-icons/fa';
import { MobxComponent } from '../../../../../mobx/components';
import { UI } from '../../../../../core/ui';
import { getMenuApiErrorMessage,
	notifyMenuSaveFailure, refreshOriginalMenusFromApi } from './menuApiOriginal';
import { colourFor, initialsFor } from '../../../../components/menu-presence';

type MenuVersion = T.API.MenuVersion;

const ALL_MENUS = '';

interface Props {
	autoOpen?: boolean;
	hideTrigger?: boolean;
	onClose?: () => void;
	menuId?: string;
}

interface State {
	active: boolean;
	loading: boolean;
	restoring: boolean;
	versions: MenuVersion[];
	currentSequence: number;
	loadError: string;
	scope: string;
	confirming: MenuVersion | null;
	copyingFrom: MenuVersion | null;
	copyName: string;
	copySubmitting: boolean;
	menuVersion: number;
	comparing: MenuVersion | null;
	compareAgainst: string;
	diff: T.API.MenuVersionDiffResponse | null;
	diffLoading: boolean;
	diffError: string;
	confirmDiff: T.API.MenuVersionDiffResponse | null;
	confirmDiffLoading: boolean;
	confirmReason: string;
	listAtTop: boolean;
	listAtBottom: boolean;
	hoveredVersionId: string | null;
	highlightedVersionId: string | null;
	matchesCurrentId: string | null;
	currentCheckedId: string | null;
}

const COMPARE_CURRENT = '';

const COMPARE_PREVIOUS = '__previous__';

function renderScrollFade(edge: 'top' | 'bottom') {
	return (
		<div
			style={{
				position: 'absolute',
				left: 0,
				right: 0,
				[edge]: 0,
				height: 24,
				pointerEvents: 'none',
				background: `linear-gradient(to ${edge === 'top' ? 'bottom' : 'top'}, #fff, rgba(255,255,255,0))`,
			}}
		/>
	);
}

const MAX_MENU_NAMES = 3;

const MAX_NAMED_MENUS = 2;

const VERSION_LIST_MAX_HEIGHT = 420;

const REBUILD_POLL_ATTEMPTS = 8;
const REBUILD_POLL_INTERVAL_MS = 600;

function sleep(ms: number): Promise<void> {
	return new Promise((resolve) => setTimeout(resolve, ms));
}

const RESTORE_APPLIED_POLL_MS = 2000;
const RESTORE_APPLIED_TIMEOUT_MS = 15 * 60 * 1000;

const pendingRestores = new Map<string, number>();

async function refreshWhenRestoreApplied(
	store: any,
	restaurantId: string,
	targetVersion: number,
	onApplied: () => void
): Promise<void> {
	// The restaurant document is what a restore or copy writes, and what the v1
	// menu list renders from, so re-reading it is correct immediately and in
	// every configuration. Done first and unconditionally: which of the two
	// menu views is on screen follows the dashboard version, while everything
	// below follows the menu schema, and the two do not have to agree.
	//
	// Guarded, because this runs from a promise nobody awaits: a throw here
	// would disappear into an unhandled rejection and look like the write
	// silently failing.
	try {
		await refreshRestaurantMenus(store, restaurantId);
	} catch (e) {
		UI.notification.warning('Saved, but the page could not reload. Refresh to see it.');
	}

	if (!store.isNewMenuSchema) {
		onApplied();
		return;
	}

	pendingRestores.set(restaurantId, targetVersion);
	const deadline = Date.now() + RESTORE_APPLIED_TIMEOUT_MS;

	while (Date.now() < deadline) {
		await sleep(RESTORE_APPLIED_POLL_MS);

		// A later restore took over, or the operator switched restaurant.
		if (pendingRestores.get(restaurantId) !== targetVersion) return;
		if (store.restaurant?._id !== restaurantId) {
			pendingRestores.delete(restaurantId);
			return;
		}

		try {
			const res = await store.api.menu_versions_list(restaurantId, 1);
			if ((res?.synced_menu_version ?? 0) < targetVersion) continue;
		} catch (e) {
			continue;
		}

		pendingRestores.delete(restaurantId);
		await refreshMenusAfterRestore(store, restaurantId, 0);
		onApplied();
		return;
	}

	if (pendingRestores.get(restaurantId) === targetVersion) {
		pendingRestores.delete(restaurantId);
		UI.notification.warning(
			'Saved, but the menu is taking longer than usual to update. Reload the page shortly.',
			{ timeout: 10000 }
		);
	}
}

const RESTAURANT_READ_ATTEMPTS = 6;
const RESTAURANT_READ_INTERVAL_MS = 700;

/**
 * Re-reads the restaurant document into the store.
 *
 * What a page reload would do, for the stores that read their menus from that
 * document. The disabled flag is carried over because it is the dashboard's
 * own, and a reload would otherwise switch hidden option sets back on.
 *
 * expectMenuId retries until that menu is in what comes back. The write has
 * committed by then, but the read can still be served ahead of it, and storing
 * a document without the new menu is indistinguishable from the copy failing.
 */
async function refreshRestaurantMenus(
	store: any,
	restaurantId: string,
	expectMenuId?: string
): Promise<boolean> {
	const disabledById = new Map(
		(store.restaurant?.option_sets || []).map((set: any) => [set._id, set.disabled])
	);

	for (let attempt = 0; attempt < RESTAURANT_READ_ATTEMPTS; attempt++) {
		const restaurant = await store.api.restaurant_find(restaurantId);
		const menus = Array.isArray(restaurant?.menus) ? restaurant.menus : [];
		const ready = !expectMenuId || menus.some((menu: any) => menu?._id === expectMenuId);

		if (!ready && attempt < RESTAURANT_READ_ATTEMPTS - 1) {
			await sleep(RESTAURANT_READ_INTERVAL_MS);
			continue;
		}

		const update: Record<string, unknown> = {};
		if (Array.isArray(restaurant?.menus)) update.menus = restaurant.menus;
		if (Array.isArray(restaurant?.dish_tags)) update.dish_tags = restaurant.dish_tags;
		if (Array.isArray(restaurant?.option_sets)) {
			update.option_sets = restaurant.option_sets.map((set: any) =>
				disabledById.get(set._id) !== undefined
					? { ...set, disabled: disabledById.get(set._id) }
					: set
			);
		}
		if (Object.keys(update).length > 0) store.updateRestaurant(update);
		return ready;
	}
	return false;
}

async function refreshMenusAfterRestore(
	store: any,
	restaurantId: string,
	expectedMinMenus: number
): Promise<void> {
	const isOriginal = store.organisation?.menu === 'original';

	for (let attempt = 0; attempt < REBUILD_POLL_ATTEMPTS; attempt++) {
		await sleep(REBUILD_POLL_INTERVAL_MS);

		const got = await store.api.menu_get_menus(restaurantId);
		const menus = got?.menus;
		if (!Array.isArray(menus)) continue;

		// Fewer menus than the restored version had means the rebuild is still
		// in flight. Writing that to the store is what blanks the screen.
		const ready = menus.length >= expectedMinMenus;
		if (!ready && attempt < REBUILD_POLL_ATTEMPTS - 1) continue;

		if (isOriginal) {
			await refreshOriginalMenusFromApi(store, restaurantId);
			return;
		}

		// The standard schema returns modifiers with their options as ids; the
		// v2 view expects them resolved to objects. Mirrors the v2 option-set
		// list, which is the shape that view is built against.
		const options = got.options || (got.option_sets || []).map((o: any) => o.options).flat() || [];
		const optionById = new Map((options || []).map((o: any) => [o.id, o]));

		const modifiers = got.modifiers || got.option_sets || [];
		modifiers.forEach((modifier: any) => {
			if (Array.isArray(modifier.options) && modifier.options.length) {
				modifier.options = modifier.options
					.map((id: any) => (typeof id === 'string' ? optionById.get(id) ?? null : id))
					.filter(Boolean);
			}
		});

		const update: Record<string, unknown> = {
			menus_v2: menus,
			option_sets_v2: modifiers,
		};
		if (Array.isArray(got.item_tags)) update.dish_tags = got.item_tags;
		else if (Array.isArray(got.dish_tags)) update.dish_tags = got.dish_tags;

		store.updateRestaurant(update);
		return;
	}
}

function describeActor(version: MenuVersion): string {
	const { actor, source } = version;
	if (actor?.name) return actor.name;
	if (actor?.email) return actor.email;
	if (actor?.api_key_name) return `${actor.api_key_name} (integration)`;
	return source === 'external' ? 'Admin dashboard (user not recorded)' : 'Unknown';
}

function dayLabel(iso: string): string {
	const at = moment(iso);
	if (!at.isValid()) return iso;

	const dated = at.isSame(moment(), 'year') ? 'D MMM' : 'D MMM YYYY';
	return at.calendar(null, { sameDay: '[Today]', lastDay: '[Yesterday]', lastWeek: dated, sameElse: dated });
}

function clockTime(iso: string): string {
	const at = moment(iso);
	return at.isValid() ? at.format(FORMATS.moment.localized.time) : iso;
}

function relativeAgo(iso: string): string {
	const at = moment(iso);
	return at.isValid() ? at.fromNow() : '';
}

function actorName(version: MenuVersion): string {
	const { actor, source } = version;
	if (actor?.name) return actor.name;
	if (actor?.email) return actor.email;
	if (actor?.api_key_name) return `${actor.api_key_name} (integration)`;
	return source === 'external' ? 'Dashboard' : 'Unknown';
}

/** One menu's entry in a version, for naming a row in a filtered list. */
function scopedSummaries(version: MenuVersion, menuID: string): MenuVersionMenuSummary[] {
	return (version.menu_summaries || []).filter((m) => m.id === menuID);
}

/**
 * Names a row.
 *
 * isEarliest is only passed for the all-menus view. Filtered to one menu, every
 * row is named "<Menu> V<n>" including the oldest, because the menu's own
 * number already says it is the first one — and the store's earliest version is
 * not the menu's, which is the whole point for a menu created later.
 */
function versionName(version: MenuVersion, changed: MenuVersionMenuSummary[], isEarliest: boolean): string | null {
	if (isEarliest) return 'Initial version';
	if (changed.length === 0) return null;

	if (changed.length > MAX_NAMED_MENUS) return `${changed.length} menus updated`;

	return changed
		.map((m) => (m.number ? `${m.name} V${m.number}` : m.name))
		.join(' · ');
}

function describeOperation(version: MenuVersion): string {
	const op = version.operation || '';

	if (op === 'external_update') return 'Saved from the dashboard';
	if (op === 'pre_restore') return 'Saved before a restore';
	if (op === 'restore' || op === 'restore_menu') return 'Restored an earlier version';
	if (op.includes('/menus/import')) return 'Menu imported';

	const [method, path = ''] = op.split(' ');
	const entity = path.includes('/items')
		? 'Items'
		: path.includes('/categories')
			? 'Categories'
			: path.includes('/modifiers') || path.includes('/option_sets')
				? 'Modifiers'
				: path.includes('/item_tags') || path.includes('/dish_tags')
					? 'Item tags'
					: path.includes('/menus')
						? 'Menu'
						: '';
	if (!entity) return 'Menu changed';

	const verb =
		method === 'POST' ? 'added' : method === 'DELETE' ? 'deleted' : 'updated';
	return `${entity} ${verb}`;
}

function versionBadge(version: MenuVersion): { label: string; colour: string; background: string; title: string } | null {
	if (version.operation === 'pre_restore') {
		return {
			label: 'Before restore',
			colour: '#33566f',
			background: '#e7eff5',
			title: 'Saved automatically just before a restore. Restore this one to undo that restore.',
		};
	}
	if (version.restored_from) {
		return {
			label: 'Restored',
			colour: '#7a5b00',
			background: '#fbf3dd',
			title: 'Created by restoring an earlier version.',
		};
	}
	return null;
}

function describeDiffSummary(summary: T.API.MenuVersionDiffSummary): string {
	const parts: string[] = [];
	if (summary.added) parts.push(`${summary.added} added`);
	if (summary.removed) parts.push(`${summary.removed} removed`);
	if (summary.modified) parts.push(`${summary.modified} edited`);
	if (parts.length === 0) return 'Nothing will change';
	return `This will change ${parts.join(', ')}`;
}

function describeWhen(iso: string): string {
	const at = moment(iso);
	if (!at.isValid()) return iso;
	return `${at.format(FORMATS.moment.localized.datetime)} (${at.fromNow()})`;
}

function menuHashIn(version: MenuVersion, menuId: string): string | undefined {
	return (version.menu_summaries || []).find((m) => m.id === menuId)?.hash;
}

function menusInHistory(versions: MenuVersion[]): Array<{ id: string; name: string }> {
	const seen = new Map<string, string>();
	for (const version of versions) {
		for (const summary of version.menu_summaries || []) {
			if (!seen.has(summary.id)) seen.set(summary.id, summary.name || summary.id);
		}
	}
	const menus = Array.from(seen, ([id, name]) => ({ id, name }));

	// Two menus can carry the same name — a template imported twice, most often
	// — and picking between identical entries is guesswork. Only the ambiguous
	// ones get a suffix, so the common case stays clean.
	const byName = new Map<string, number>();
	menus.forEach((m) => byName.set(m.name, (byName.get(m.name) ?? 0) + 1));
	return menus.map((m) => (byName.get(m.name)! > 1 ? { ...m, name: `${m.name} · ${m.id.slice(0, 4)}` } : m));
}

function latestMenuNumber(versions: MenuVersion[], menuId: string): number {
	for (const version of versions) {
		const summary = (version.menu_summaries || []).find((m) => m.id === menuId);
		if (summary && summary.number) return summary.number;
	}
	return 0;
}

/**
 * The versions in which one menu actually changed.
 *
 * Versions arrive newest first, so a menu changed in version i when its hash
 * differs from the same menu's hash in version i+1. Comparing hashes is why
 * grouping never has to load menu content.
 *
 * The oldest version is always included: there is nothing older to compare it
 * against, and it is the furthest back that menu can be taken.
 */
function versionsAffecting(versions: MenuVersion[], menuId: string): MenuVersion[] {
	return versions.filter((version, i) => {
		const hash = menuHashIn(version, menuId);
		if (hash === undefined) return false;

		const older = versions[i + 1];
		if (!older) return true;
		return hash !== menuHashIn(older, menuId);
	});
}

type MenuVersionMenuSummary = T.API.MenuVersionMenuSummary;
type EntityChange = T.API.MenuVersionEntityChange;
type FieldChange = T.API.MenuVersionFieldChange;

const CHANGE_STYLE: Record<EntityChange['kind'], { label: string; colour: string; background: string }> = {
	added: { label: 'Added', colour: '#1f7a44', background: '#e6f4ec' },
	removed: { label: 'Removed', colour: '#a12727', background: '#fbeaea' },
	modified: { label: 'Changed', colour: '#7a5b00', background: '#fbf3dd' },
};

const LEVEL_LABELS: Record<string, string[]> = {
	Menus: ['Menu', 'Category', 'Item'],
	Modifiers: ['Modifier', 'Option'],
	'Item tags': ['Item tag'],
};

function describeSummary(summary: T.API.MenuVersionDiffSummary): string {
	const parts: string[] = [];
	if (summary.added) parts.push(`${summary.added} added`);
	if (summary.removed) parts.push(`${summary.removed} removed`);
	if (summary.modified) parts.push(`${summary.modified} changed`);
	return parts.join(' · ');
}

function formatValue(value: any): string {
	if (value === undefined || value === null) return '';
	if (Array.isArray(value)) return value.join(', ');
	if (typeof value === 'boolean') return value ? 'yes' : 'no';
	if (typeof value === 'string' && ISO_DATE.test(value)) return moment(value).format(FORMATS.moment.localized.datetime);
	return String(value);
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

function FieldPath(props: { path: string }) {
	const parts = props.path.split('.');
	const leaf = parts.pop();

	return (
		<span style={{ fontFamily: 'monospace', fontSize: 11 }}>
			{parts.length > 0 && <span style={{ color: '#aaa' }}>{parts.join('.')}.</span>}
			<span style={{ color: '#555' }}>{leaf}</span>
		</span>
	);
}

function ValueChip(props: { text: string; tone: 'before' | 'after' }) {
	const before = props.tone === 'before';
	return (
		<span
			style={{
				background: before ? '#fbeaea' : '#e6f4ec',
				color: before ? '#a12727' : '#1f7a44',
				borderRadius: 3,
				padding: '1px 5px',
				fontSize: 11,
				fontFamily: 'monospace',
				textDecoration: before ? 'line-through' : 'none',
				wordBreak: 'break-word',
			}}
		>
			{props.text}
		</span>
	);
}

function FieldRow(props: { change: FieldChange }) {
	const { change } = props;
	const from = formatValue(change.from);
	const to = formatValue(change.to);

	return (
		<div
			style={{
				display: 'flex',
				alignItems: 'baseline',
				flexWrap: 'wrap',
				gap: 6,
				padding: '2px 0',
			}}
		>
			<FieldPath path={change.field} />
			{from !== '' && <ValueChip text={from} tone="before" />}
			{from !== '' && to !== '' && <span style={{ color: '#bbb', fontSize: 11 }}>→</span>}
			{to !== '' && <ValueChip text={to} tone="after" />}
			{from === '' && to === '' && (
				<span style={{ fontSize: 11, color: '#8a8a8a', fontStyle: 'italic' }}>
					{change.kind === 'set' ? 'added' : change.kind === 'cleared' ? 'removed' : 'changed'}
				</span>
			)}
			{from !== '' && to === '' && change.kind === 'cleared' && (
				<span style={{ fontSize: 11, color: '#8a8a8a', fontStyle: 'italic' }}>removed</span>
			)}
		</div>
	);
}

interface EntityNodeProps {
	change: EntityChange;
	depth: number;
	labels: string[];
}

class EntityNode extends React.Component<EntityNodeProps, { open: boolean }> {
	state = { open: true };

	render() {
		const { change, depth, labels } = this.props;
		const style = CHANGE_STYLE[change.kind];
		const children = change.children ?? [];
		const fields = change.fields ?? [];
		const level = labels[depth] ?? '';

		return (
			<div
				style={{
					paddingLeft: depth === 0 ? 0 : 12,
					borderLeft: depth === 0 ? 'none' : '1px solid #ececec',
					marginLeft: depth === 0 ? 0 : 3,
				}}
			>
				<div style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '3px 0' }}>
					{children.length > 0 ? (
						<span
							onClick={() => this.setState({ open: !this.state.open })}
							style={{ cursor: 'pointer', color: '#999', fontSize: 10, width: 10 }}
						>
							{this.state.open ? '▼' : '▶'}
						</span>
					) : (
						<span style={{ width: 10 }} />
					)}
					<span
						style={{
							background: style.background,
							color: style.colour,
							borderRadius: 3,
							padding: '1px 6px',
							fontSize: 10,
							fontWeight: 600,
							textTransform: 'uppercase',
							letterSpacing: 0.3,
							whiteSpace: 'nowrap',
						}}
					>
						{style.label}
					</span>
					<span style={{ fontWeight: 600, fontSize: 13 }}>{change.name}</span>
					{level && <span style={{ fontSize: 11, color: '#aaa' }}>{level}</span>}
					{children.length > 0 && !this.state.open && (
						<span style={{ fontSize: 11, color: '#aaa' }}>
							{children.length} change{children.length === 1 ? '' : 's'} inside
						</span>
					)}
				</div>

				{this.state.open && (
					<div style={{ paddingLeft: 18 }}>
						{fields.map((field) => (
							<FieldRow key={field.field} change={field} />
						))}
						{children.map((child) => (
							<EntityNode
								key={child.id}
								change={child}
								depth={depth + 1}
								labels={labels}
							/>
						))}
					</div>
				)}
			</div>
		);
	}
}

function renderChangeGroup(title: string, changes: EntityChange[]) {
	if (!changes || changes.length === 0) return null;

	return (
		<div key={title} style={{ marginBottom: 18 }}>
			<div
				style={{
					fontSize: 11,
					fontWeight: 600,
					color: '#999',
					letterSpacing: 0.5,
					marginBottom: 6,
					paddingBottom: 4,
					borderBottom: '1px solid #f0f0f0',
				}}
			>
				{title.toUpperCase()}
			</div>
			{changes.map((change) => (
				<EntityNode
					key={change.id}
					change={change}
					depth={0}
					labels={LEVEL_LABELS[title] ?? []}
				/>
			))}
		</div>
	);
}

interface PickerOption {
	value: string;
	label: string;
	detail?: string;
	tag?: string;
	startsGroup?: boolean;
}

interface PickerProps {
	value: string;
	options: PickerOption[];
	onChange: (value: string) => void;
}

const PICKER_TAG_STYLE: React.CSSProperties = {
	background: '#eef2f6',
	color: '#5b6b7b',
	borderRadius: 3,
	padding: '1px 6px',
	fontSize: 11,
	fontWeight: 600,
	whiteSpace: 'nowrap',
};

/**
 * A dropdown drawn by the page rather than by the operating system.
 *
 * A native select opens an OS menu — on a Mac, a dark system list over a white
 * modal — and every option is one line of plain text. Drawn here, the list
 * matches the modal it belongs to and an option can carry a second line and a
 * tag, which is how a menu shows its version number and how many versions it
 * has beside its name.
 *
 * DropDown supplies the positioning and click-outside handling every other
 * dropdown in the dashboard uses. What this adds is the field, the rows, and
 * keyboard control — a native select can be driven without a mouse, and a
 * replacement that could not would be a step backwards.
 */
class Picker extends React.PureComponent<PickerProps, { open: boolean; active: string }> {
	state = { open: false, active: '' };

	closedAt = 0;

	openList = () => {
		if (Date.now() - this.closedAt < 250) return;
		this.setState({ open: true, active: this.props.value });
	};

	closeList = () => {
		this.closedAt = Date.now();
		this.setState({ open: false });
	};

	select = (value: string) => {
		this.closedAt = Date.now();
		this.setState({ open: false });
		if (value !== this.props.value) this.props.onChange(value);
	};

	onKeyDown = (e: React.KeyboardEvent) => {
		const { options, value } = this.props;
		const { open, active } = this.state;

		if (e.key === 'Escape') {
			if (open) {
				e.preventDefault();
				this.closeList();
			}
			return;
		}

		if (e.key === 'Enter' || e.key === ' ') {
			e.preventDefault();
			if (open) this.select(active || value);
			else this.setState({ open: true, active: value });
			return;
		}

		if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
		e.preventDefault();
		if (!open) {
			this.setState({ open: true, active: value });
			return;
		}

		const from = options.findIndex((o) => o.value === (active || value));
		const next = (from === -1 ? 0 : from) + (e.key === 'ArrowDown' ? 1 : -1);
		const option = options[Math.min(options.length - 1, Math.max(0, next))];
		if (option) this.setState({ active: option.value });
	};

	renderOptions() {
		const { options, value } = this.props;
		const { active } = this.state;

		return (
			<div style={{ maxHeight: 280, overflowY: 'auto', padding: 6 }}>
				{options.map((option) => {
					const selected = option.value === value;
					return (
						<div key={option.value || 'none'}>
							{option.startsGroup && (
								<div style={{ height: 1, background: '#eee', margin: '6px 4px' }} />
							)}
							<div
								onClick={() => this.select(option.value)}
								onMouseEnter={() => this.setState({ active: option.value })}
								style={{
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'space-between',
									gap: 10,
									padding: '7px 9px',
									borderRadius: 4,
									cursor: 'pointer',
									background: option.value === active ? '#f1f5f9' : 'transparent',
								}}
							>
								<div style={{ minWidth: 0 }}>
									<div
										style={{
											fontSize: 13,
											fontWeight: selected ? 600 : 400,
											whiteSpace: 'nowrap',
											overflow: 'hidden',
											textOverflow: 'ellipsis',
										}}
									>
										{option.label}
									</div>
									{option.detail && (
										<div style={{ fontSize: 11, color: '#8a8a8a', marginTop: 2 }}>
											{option.detail}
										</div>
									)}
								</div>
								<div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
									{option.tag && <span style={PICKER_TAG_STYLE}>{option.tag}</span>}
									<FaCheck
										style={{ fontSize: 11, color: selected ? '#3e7bb6' : 'transparent' }}
									/>
								</div>
							</div>
						</div>
					);
				})}
			</div>
		);
	}

	render() {
		const { options, value } = this.props;
		const { open } = this.state;
		const selected = options.find((o) => o.value === value);

		return (
			<DropDown
				active={open}
				open={this.openList}
				close={this.closeList}
				offset={4}
				content={this.renderOptions()}
			>
				<div
					tabIndex={0}
					onKeyDown={this.onKeyDown}
					style={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						gap: 10,
						padding: '9px 12px',
						border: `1px solid ${open ? '#9aa7b4' : '#dcdcdc'}`,
						borderRadius: 4,
						background: '#fff',
						cursor: 'pointer',
						outline: 'none',
					}}
				>
					<span
						style={{
							fontSize: 14,
							whiteSpace: 'nowrap',
							overflow: 'hidden',
							textOverflow: 'ellipsis',
						}}
					>
						{selected ? selected.label : 'Select…'}
					</span>
					<span
						style={{
							display: 'flex',
							alignItems: 'center',
							gap: 8,
							flexShrink: 0,
							color: '#8a8a8a',
						}}
					>
						{selected && selected.tag && <span style={PICKER_TAG_STYLE}>{selected.tag}</span>}
						<FaChevronDown style={{ fontSize: 11 }} />
					</span>
				</div>
			</DropDown>
		);
	}
}

@inject('store')
@observer
export class MenuVersionHistory extends MobxComponent<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = {
			active: !!props.autoOpen,
			loading: false,
			restoring: false,
			versions: [],
			currentSequence: 0,
			loadError: '',
			scope: props.menuId ?? ALL_MENUS,
			confirming: null,
			copyingFrom: null,
			copyName: '',
			copySubmitting: false,
			confirmDiff: null,
			confirmDiffLoading: false,
			confirmReason: '',
			listAtTop: true,
			listAtBottom: true,
			hoveredVersionId: null,
			highlightedVersionId: null,
			matchesCurrentId: null,
			currentCheckedId: null,
			menuVersion: 0,
			comparing: null,
			compareAgainst: COMPARE_PREVIOUS,
			diff: null,
			diffLoading: false,
			diffError: '',
		};
	}

	componentDidMount() {
		if (this.state.active) this.load();
	}

	open = () => this.setState({ active: true, scope: this.props.menuId ?? ALL_MENUS }, this.load);

	close = () => {
		this.setState({ active: false, confirming: null, copyingFrom: null, loadError: '', comparing: null, diff: null });
		this.props.onClose?.();
	};

	load = async () => {
		const { store } = this.injected;
		const restaurantId = store.restaurant?._id;
		if (!restaurantId) return;

		this.setState({ loading: true, loadError: '' });
		try {
			const res = await store.api.menu_versions_list(restaurantId);
			this.setState({
				matchesCurrentId: null,
				currentCheckedId: null,
				versions: res?.versions ?? [],
				currentSequence: res?.current_sequence ?? 0,
				menuVersion: res?.menu_version ?? 0,
				loading: false,
			});
			void this.checkNewestAgainstCurrent();
		} catch (e) {
			this.setState({ loading: false, loadError: getMenuApiErrorMessage(e) });
		}
	};

	async checkNewestAgainstCurrent() {
		const { store } = this.injected;
		const restaurantId = store.restaurant?._id;
		const newest = this.visibleVersions()[0];
		if (!restaurantId || !newest) return;

		try {
			const diff = await store.api.menu_versions_diff({
				restaurant_id: restaurantId,
				version_id: newest._id,
				menu_id: this.state.scope === ALL_MENUS ? undefined : this.state.scope,
			});
			const unchanged = diff.summary.added + diff.summary.removed + diff.summary.modified === 0;
			this.setState({ matchesCurrentId: unchanged ? newest._id : null, currentCheckedId: newest._id });
		} catch (e) {
			this.setState({ matchesCurrentId: null, currentCheckedId: null });
		}
	}

	visibleVersions(): MenuVersion[] {
		const { versions, scope } = this.state;
		return scope === ALL_MENUS ? versions : versionsAffecting(versions, scope);
	}

	scopeName(): string {
		const { scope, versions } = this.state;
		if (scope === ALL_MENUS) return 'all menus';
		const menu = menusInHistory(versions).find((m) => m.id === scope);
		return menu ? menu.name : 'this menu';
	}

	defaultCopyName(version: MenuVersion): string {
		const summary = (version.menu_summaries || []).find((m) => m.id === this.state.scope);
		const name = summary?.name || 'Menu';
		const at = moment(version.created_at);
		return at.isValid() ? `${name} (restored ${at.format('D MMM')})` : `${name} (restored)`;
	}

	askCopy = (version: MenuVersion) => {
		if (!this.copyAvailable()) return;
		this.setState({
			copyingFrom: version,
			copyName: this.defaultCopyName(version),
			confirming: null,
			comparing: null,
			diff: null,
		});
	};

	/**
	 * Whether adding a copy is on offer.
	 *
	 * One menu at a time: copying a whole store would duplicate every menu in
	 * it. Same permission as restoring — not destructive, but still restoring.
	 */
	copyAvailable(): boolean {
		return this.state.scope !== ALL_MENUS && this.canRestore;
	}

	copyMenu = async (version: MenuVersion, name: string) => {
		const { store } = this.injected;
		const restaurantId = store.restaurant?._id;
		const { scope } = this.state;
		if (!restaurantId || scope === ALL_MENUS || !this.canRestore) return;

		// The dialog stays up, with the button saying what is happening, until the
		// write comes back. It is about a request long, and closing on the click
		// left a stretch where nothing on screen had changed. A failure then has
		// somewhere to be reported, and the operator can try again.
		this.setState({ copySubmitting: true });

		try {
			const res = await store.api.menu_versions_copy_menu({
				restaurant_id: restaurantId,
				version_id: version._id,
				menu_id: scope,
				name: name.trim(),
				reason: this.state.confirmReason,
			});
			this.setState({ copySubmitting: false });
			this.close();

			// The service writes both databases before replying, so there is
			// nothing to wait for — only to re-read whichever one this page
			// renders from.
			const shown = await refreshRestaurantMenus(store, restaurantId, res.menu_id);
			if (store.isNewMenuSchema) await refreshMenusAfterRestore(store, restaurantId, 0);

			if (shown) UI.notification.success(`Added "${res.name}"`);
			else UI.notification.warning(`Added "${res.name}". Refresh the page to see it.`);
		} catch (e) {
			this.setState({ copySubmitting: false });
			notifyMenuSaveFailure(e);
		}
	};

	restore = async (version: MenuVersion) => {
		const { store } = this.injected;
		const restaurantId = store.restaurant?._id;
		if (!restaurantId) return;

		const { scope } = this.state;
		this.setState({ restoring: true, confirming: null });
		try {
			const res = await store.api.menu_versions_restore({
				restaurant_id: restaurantId,
				version_id: version._id,
				expected_menu_version: this.state.menuVersion,
				menu_id: scope === ALL_MENUS ? undefined : scope,
				reason: this.state.confirmReason,
			});

			const readded = (res?.restored_modifiers ?? 0) + (res?.restored_item_tags ?? 0);
			this.setState({ restoring: false });
			this.close();
			UI.notification.info('Restoring…', { timeout: 8000 });

			void refreshWhenRestoreApplied(store, restaurantId, res?.menu_version ?? 0, () => {
				UI.notification.success(
					readded > 0 ? `Menu restored, with ${readded} linked item(s)` : 'Menu restored'
				);
			});
		} catch (e) {
			this.setState({ restoring: false });
			await this.load();

			// Someone else saved first. That is the one failure the operator can
			// act on, so it gets the dialog that names them rather than a toast
			// that scrolls away. It is also the only conflict a v2 store can
			// reach: its ordinary edits are targeted and do not take the lock.
			if ((e as any)?.status === 409) {
				this.raiseRestoreConflict(restaurantId);
				return;
			}
			notifyMenuSaveFailure(e);
		}
	};

	/**
	 * Names whoever saved first, using the version their save produced.
	 *
	 * Menu-service refuses with a version number, not an identity — it does not
	 * read the restaurant's author stamp on that path. The history was just
	 * reloaded, so its newest entry is that save.
	 */
	raiseRestoreConflict(restaurantId: string) {
		const newest = this.state.versions[0];
		this.injected.store.menu_conflict.raise(restaurantId, {
			outcome: 1,
			message: 'Someone else changed this menu since this list was loaded.',
			conflict: 'menu_version',
			last_updated_by: newest?.actor?.id
				? {
						id: newest.actor.id,
						email: newest.actor.email ?? '',
						name: newest.actor.name,
						at: newest.created_at,
					}
				: undefined,
			menu_version: this.state.menuVersion,
		});
	}

	compare = (version: MenuVersion, against: string = COMPARE_PREVIOUS) => {
		this.setState({ comparing: version, compareAgainst: against, diff: null, diffError: '' }, this.loadDiff);
	};

	loadDiff = async () => {
		const { store } = this.injected;
		const restaurantId = store.restaurant?._id;
		const { comparing, compareAgainst, scope } = this.state;
		if (!restaurantId || !comparing) return;

		// Against the previous version the direction flips — previous to this —
		// so "added" and "removed" describe what this version itself changed.
		let versionId = comparing._id;
		let against: string | undefined = compareAgainst || undefined;
		if (compareAgainst === COMPARE_PREVIOUS) {
			const previous = this.previousVersion(comparing);
			if (!previous) {
				this.setState({ diff: null, diffLoading: false, diffError: '' });
				return;
			}
			versionId = previous._id;
			against = comparing._id;
		}

		this.setState({ diffLoading: true, diffError: '' });
		try {
			const diff = await store.api.menu_versions_diff({
				restaurant_id: restaurantId,
				version_id: versionId,
				against,
				menu_id: scope === ALL_MENUS ? undefined : scope,
			});
			this.setState({ diff, diffLoading: false });
		} catch (e) {
			this.setState({ diffLoading: false, diffError: getMenuApiErrorMessage(e) });
		}
	};

	previousVersion(version: MenuVersion): MenuVersion | undefined {
		const visible = this.visibleVersions();
		const index = visible.findIndex((v) => v._id === version._id);
		return index >= 0 ? visible[index + 1] : undefined;
	}

	renderScopeSelect() {
		const options = this.scopeOptions();
		if (options.length <= 1) return null;

		return (
			<div style={{ marginBottom: 16 }}>
				<label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
					Choose your menu
				</label>
				<Picker
					value={this.state.scope}
					options={options}
					onChange={(scope) =>
						this.setState({ scope, matchesCurrentId: null, currentCheckedId: null }, () =>
							this.checkNewestAgainstCurrent()
						)
					}
				/>
				<p style={{ fontSize: 12, color: '#8a8a8a', marginTop: 6 }}>
					{this.state.scope === ALL_MENUS
						? 'Restores every menu in this store. Choose one menu to add a copy of it instead.'
						: 'Restores this menu only.'}
				</p>
			</div>
		);
	}

	/** True when the scoped menu exists but no version holds it yet. */
	scopedMenuIsNew(): boolean {
		const { scope, versions } = this.state;
		if (scope === ALL_MENUS) return false;
		return !versions.some((v) => (v.menu_summaries || []).some((m) => m.id === scope));
	}

	scopeOptions(): PickerOption[] {
		const { scope, versions } = this.state;
		const menus = menusInHistory(versions);

		// A menu created minutes ago is in no version yet — the first capture is
		// a debounce window behind the save. Without this the picker cannot name
		// the menu the caller asked for and falls back to its empty placeholder,
		// which reads as though nothing is selected.
		if (this.scopedMenuIsNew()) {
			const live = (this.injected.store.restaurant?.menus || []).find((m: any) => m._id === scope);
			if (live) menus.push({ id: scope, name: live.name || scope });
		}

		if (menus.length === 0) return [];

		const live = new Set(((versions[0] || {}).menu_summaries || []).map((m) => m.id));
		const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

		return [
			{
				value: ALL_MENUS,
				label: 'All menus',
				detail: `${plural(versions.length, 'version')} across ${plural(menus.length, 'menu')}`,
			},
			...menus.map((menu, i) => {
				const kept = plural(versionsAffecting(versions, menu.id).length, 'version');
				const number = latestMenuNumber(versions, menu.id);
				return {
					value: menu.id,
					label: menu.name,
					detail: live.has(menu.id) ? kept : `Deleted · ${kept}`,
					tag: number ? `V${number}` : undefined,
					startsGroup: i === 0,
				};
			}),
		];
	}

	changedMenus(version: MenuVersion): MenuVersionMenuSummary[] {
		const { versions } = this.state;
		const index = versions.findIndex((v) => v._id === version._id);
		const older = index >= 0 ? versions[index + 1] : undefined;

		return (version.menu_summaries || []).filter(
			(summary) => !older || menuHashIn(older, summary.id) !== summary.hash
		);
	}

	private rowElements = new Map<string, HTMLDivElement>();
	private highlightTimer?: ReturnType<typeof setTimeout>;

	componentWillUnmount() {
		if (this.highlightTimer) clearTimeout(this.highlightTimer);
	}

	jumpToVersion = (versionId: string) => {
		const row = this.rowElements.get(versionId);
		if (!row) return;

		row.scrollIntoView({ behavior: 'smooth', block: 'center' });
		this.setState({ highlightedVersionId: versionId });
		if (this.highlightTimer) clearTimeout(this.highlightTimer);
		this.highlightTimer = setTimeout(() => this.setState({ highlightedVersionId: null }), 2500);
	};

	get canRestore(): boolean {
		const restrictions = this.injected.store.auth?.item?.restrictions;
		if (!restrictions) return true;
		return !!restrictions.restaurant?.menus_detail?.restore;
	}

	askConfirm = (version: MenuVersion) => {
		if (!this.canRestore) return;
		this.setState({ confirming: version, confirmDiff: null, confirmDiffLoading: true, confirmReason: '' }, async () => {
			const { store } = this.injected;
			const restaurantId = store.restaurant?._id;
			const { scope } = this.state;
			if (!restaurantId) return;
			try {
				const diff = await store.api.menu_versions_diff({
					restaurant_id: restaurantId,
					version_id: version._id,
					menu_id: scope === ALL_MENUS ? undefined : scope,
				});
				// Ignore a reply for a dialog the operator already dismissed.
				if (this.state.confirming?._id !== version._id) return;
				this.setState({ confirmDiff: diff, confirmDiffLoading: false });
			} catch (e) {
				if (this.state.confirming?._id !== version._id) return;
				this.setState({ confirmDiffLoading: false });
			}
		});
	};

	renderActor(version: MenuVersion) {
		const name = actorName(version);
		return (
			<span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
				<span
					style={{
						width: 20,
						height: 20,
						borderRadius: '50%',
						background: colourFor(version.actor?.id || name),
						color: '#fff',
						fontSize: 9,
						fontWeight: 600,
						display: 'inline-flex',
						alignItems: 'center',
						justifyContent: 'center',
						flexShrink: 0,
					}}
				>
					{initialsFor(name)}
				</span>
				<span style={{ fontSize: 13, color: '#666' }}>{name}</span>
			</span>
		);
	}

	renderCurrentRow() {
		const { versions, restoring, matchesCurrentId, currentCheckedId } = this.state;
		const newest = versions[0];
		if (!newest || currentCheckedId !== newest._id || matchesCurrentId === newest._id) return null;

		return (
			<div
				style={{
					border: '1px solid #e4ecf4',
					background: '#f7fbff',
					borderRadius: 4,
					padding: '10px 14px',
					marginBottom: 10,
					display: 'flex',
					alignItems: 'center',
					gap: 12,
				}}
			>
				<div style={{ flexGrow: 1, minWidth: 0 }}>
					<div style={{ fontWeight: 600 }}>Unsaved in any version</div>
					<div style={{ fontSize: 12, color: '#666' }}>
						Your live menu has changed since the newest version
					</div>
				</div>
				<Button
					size="xs"
					disabled={restoring}
					onClick={() => this.compare(newest, COMPARE_CURRENT)}
				>
					See the changes
				</Button>
			</div>
		);
	}

	// One action per row: the row itself opens the comparison. Two equal buttons
	// per row, twenty rows deep, is what made this list hard to read.
	renderVersion = (version: MenuVersion) => {
		const { scope, restoring, hoveredVersionId } = this.state;
		const changedMenus = this.changedMenus(version);
		const changed = scope === ALL_MENUS ? changedMenus.map((m) => m.name || m.id) : [];
		const shownMenus = changed.slice(0, MAX_MENU_NAMES);
		const isEarliest =
			scope === ALL_MENUS && this.state.versions[this.state.versions.length - 1]?._id === version._id;
		// Filtered to one menu, the row is named after that menu alone. Naming it
		// after everything the version touched answers a question the filter says
		// you are not asking, and past two menus it collapses to a count whose
		// supporting list only renders in the all-menus view.
		const title = versionName(version, scope === ALL_MENUS ? changedMenus : scopedSummaries(version, scope), isEarliest);
		const hovered = hoveredVersionId === version._id;
		const badge = versionBadge(version);
		const highlighted = this.state.highlightedVersionId === version._id;
		const source = version.restored_from
			? this.state.versions.find((v) => v._id === version.restored_from)
			: undefined;
		const restoredBy = this.state.versions.find((v) => v.restored_from === version._id);

		return (
			<div
				key={version._id}
				ref={(el: HTMLDivElement | null) => {
					if (el) this.rowElements.set(version._id, el);
					else this.rowElements.delete(version._id);
				}}
				onClick={() => this.compare(version)}
				onMouseEnter={() => this.setState({ hoveredVersionId: version._id })}
				onMouseLeave={() => this.setState({ hoveredVersionId: null })}
				title="See what changed in this version"
				style={{
					border: '1px solid ' + (highlighted ? '#c99a00' : hovered ? '#cfcfcf' : '#ececec'),
					background: highlighted ? '#fffbef' : hovered ? '#fafafa' : '#fff',
					transition: 'background 0.3s, border-color 0.3s',
					borderRadius: 4,
					padding: '10px 14px',
					marginBottom: 8,
					display: 'flex',
					alignItems: 'center',
					gap: 12,
					cursor: 'pointer',
				}}
			>
				<div style={{ flexGrow: 1, minWidth: 0 }}>
					<div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
						<span style={{ fontWeight: 600 }}>{title || describeOperation(version)}</span>
						<span style={{ fontSize: 12, color: '#8a8a8a' }}>
							{clockTime(version.created_at)} · {relativeAgo(version.created_at)}
						</span>
						{badge && (
							<span
								title={badge.title}
								style={{
									fontSize: 10,
									fontWeight: 600,
									textTransform: 'uppercase',
									letterSpacing: 0.3,
									color: badge.colour,
									background: badge.background,
									borderRadius: 3,
									padding: '1px 6px',
								}}
							>
								{badge.label}
							</span>
						)}
					</div>
					<div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3, flexWrap: 'wrap' }}>
						{this.renderActor(version)}
						{title && (
							<span style={{ fontSize: 12, color: '#8a8a8a' }}>· {describeOperation(version)}</span>
						)}
					</div>
					{(source || version.restored_from || restoredBy) && (
						<div style={{ fontSize: 12, color: '#8a8a8a', marginTop: 4 }}>
							{version.restored_from ? (
								source ? (
									<>
										Taken from the version of{' '}
										<a
											href="#"
											onClick={(e: any) => {
												e.preventDefault();
												e.stopPropagation();
												this.jumpToVersion(source._id);
											}}
										>
											{dayLabel(source.created_at)} at {clockTime(source.created_at)}
										</a>
									</>
								) : (
									'Taken from a version that is no longer kept'
								)
							) : (
								<>
									Restored{' '}
									<a
										href="#"
										onClick={(e: any) => {
											e.preventDefault();
											e.stopPropagation();
											this.jumpToVersion(restoredBy!._id);
										}}
									>
										{dayLabel(restoredBy!.created_at)} at {clockTime(restoredBy!.created_at)}
									</a>
								</>
							)}
						</div>
					)}
					{version.reason && (
						<div
							style={{
								fontSize: 13,
								color: '#444',
								marginTop: 5,
								paddingLeft: 8,
								borderLeft: '2px solid #e0e0e0',
								fontStyle: 'italic',
							}}
						>
							“{version.reason}”
						</div>
					)}
					{scope === ALL_MENUS && (
						<div style={{ fontSize: 12, color: '#555', marginTop: 4 }}>
							{changed.length > 0 ? (
								<>
									<span style={{ color: '#8a8a8a' }}>Changed: </span>
									{shownMenus.join(', ')}
									{changed.length > shownMenus.length &&
										` and ${changed.length - shownMenus.length} more`}
								</>
							) : (
								<span style={{ color: '#8a8a8a' }}>Modifiers or item tags only</span>
							)}
						</div>
					)}
				</div>
				{this.state.matchesCurrentId === version._id ? (
					<span
						style={{ fontSize: 12, color: '#8a8a8a', whiteSpace: 'nowrap' }}
						title="This version holds exactly what is live, so restoring it would change nothing."
					>
						Current state
					</span>
				) : this.canRestore ? (
					<>
						{this.copyAvailable() && (
							<Button
								size="xs"
								disabled={restoring}
								onClick={(e: any) => {
									e.stopPropagation();
									this.askCopy(version);
								}}
							>
								Copy
							</Button>
						)}
						<Button
							size="xs"
							color="primary-inverse"
							disabled={restoring}
							onClick={(e: any) => {
								e.stopPropagation();
								this.askConfirm(version);
							}}
						>
							Restore
						</Button>
					</>
				) : null}
			</div>
		);
	};

	renderList() {
		const { loading, loadError, restoring, scope } = this.state;

		if (restoring) {
			return (
				<div style={{ padding: 30, textAlign: 'center' }}>
					<RotateLoader size={3} color="#505050" />
					<p style={{ marginTop: 14, fontSize: 13, color: '#666' }}>Restoring the menu…</p>
				</div>
			);
		}

		if (loading) {
			return this.renderSkeleton();
		}

		if (loadError) {
			return (
				<div style={{ padding: 20, textAlign: 'center' }}>
					<p style={{ marginBottom: 12 }}>{loadError}</p>
					<Button size="xs" onClick={this.load}>
						Try again
					</Button>
				</div>
			);
		}

		const visible = this.visibleVersions();
		const total = this.state.versions.length;

		if (visible.length === 0) {
			// Three different situations, and telling the operator the wrong one
			// wastes their time. A menu saved a minute ago has no history yet;
			// saying it "has not changed in any of the 22 saved versions" is true
			// of the store and useless about the menu.
			const isNew = this.scopedMenuIsNew();
			const heading =
				scope === ALL_MENUS
					? 'No versions saved yet.'
					: isNew
						? 'No versions saved for this menu yet.'
						: `This menu has not changed in any of the ${total} saved version(s).`;
			const detail =
				scope === ALL_MENUS
					? 'Versions are saved automatically as the menu changes.'
					: isNew
						? 'The first one is saved shortly after your first change. Reopen this window to see it.'
						: 'Switch to "All menus" for the full history.';

			return (
				<div style={{ padding: 20, textAlign: 'center', color: '#666' }}>
					<p>{heading}</p>
					<p style={{ fontSize: 13, marginTop: 8 }}>{detail}</p>
				</div>
			);
		}

		const { listAtTop, listAtBottom } = this.state;

		return (
			<div>
				{scope !== ALL_MENUS && visible.length < total && (
					<p style={{ fontSize: 12, color: '#8a8a8a', marginBottom: 10 }}>
						Showing the {visible.length} versions where this menu changed, out of {total} saved
						for the store. The rest left this menu exactly as it was.
					</p>
				)}
				<div style={{ position: 'relative' }}>
					<div
						style={{ maxHeight: VERSION_LIST_MAX_HEIGHT, overflowY: 'auto', paddingRight: 4 }}
						onScroll={this.onListScroll}
					>
						{this.renderCurrentRow()}
						{this.renderGroupedVersions(visible)}
					</div>
					{!listAtTop && renderScrollFade('top')}
					{!listAtBottom && renderScrollFade('bottom')}
				</div>
			</div>
		);
	}

	onListScroll = (e: any) => {
		const el = e.currentTarget;
		const atTop = el.scrollTop <= 2;
		const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= 2;
		if (atTop !== this.state.listAtTop || atBottom !== this.state.listAtBottom) {
			this.setState({ listAtTop: atTop, listAtBottom: atBottom });
		}
	};

	renderGroupedVersions(visible: MenuVersion[]) {
		const rows: JSX.Element[] = [];
		let day = '';

		visible.forEach((version) => {
			const versionDay = dayLabel(version.created_at);
			if (versionDay !== day) {
				day = versionDay;
				rows.push(
					<div
						key={`day-${version._id}`}
						style={{
							fontSize: 11,
							fontWeight: 600,
							textTransform: 'uppercase',
							letterSpacing: 0.4,
							color: '#9a9a9a',
							margin: '14px 0 6px',
						}}
					>
						{versionDay}
					</div>
				);
			}
			rows.push(this.renderVersion(version));
		});

		return rows;
	}

	renderSkeleton() {
		const bar = (width: number, height: number) => (
			<div style={{ width, height, background: '#eee', borderRadius: 3 }} />
		);
		return (
			<div>
				{[0, 1, 2].map((i) => (
					<div
						key={i}
						style={{
							border: '1px solid #f0f0f0',
							borderRadius: 4,
							padding: '12px 14px',
							marginBottom: 8,
							display: 'flex',
							alignItems: 'center',
							gap: 12,
						}}
					>
						<div style={{ flexGrow: 1, display: 'grid', gap: 6 }}>
							{bar(120, 12)}
							{bar(180, 10)}
						</div>
						{bar(64, 24)}
					</div>
				))}
			</div>
		);
	}

	renderCompare() {
		const { comparing, compareAgainst, diff, diffLoading, diffError, versions, restoring, scope } = this.state;
		if (!comparing) return null;

		const others = versions.filter((v) => v._id !== comparing._id);
		const earliestId = scope === ALL_MENUS ? versions[versions.length - 1]?._id : undefined;

		return (
			<Modal
				active
				width={620}
				close={() => this.setState({ comparing: null, diff: null, diffError: '' })}
			>
				<ModalTitle className="round-top-sm">
					<h3 className="text-center flex-grow">What changed</h3>
				</ModalTitle>
				<ModalContent paddingtb={24} paddinglr={24}>
					<p style={{ fontSize: 13, color: '#666', marginBottom: 14 }}>
						{compareAgainst === COMPARE_PREVIOUS ? (
							<>
								What changed in the version from <strong>{describeWhen(comparing.created_at)}</strong>,
								saved by {describeActor(comparing)}, compared with the version before it.
							</>
						) : compareAgainst === COMPARE_CURRENT ? (
							<>
								How the version from <strong>{describeWhen(comparing.created_at)}</strong> differs from
								the menu as it is now. Restoring it would undo everything listed here.
							</>
						) : (
							<>
								How the version from <strong>{describeWhen(comparing.created_at)}</strong> differs from
								the version selected below.
							</>
						)}
					</p>

					<div style={{ marginBottom: 16 }}>
						<label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
							Compare against
						</label>
						<Picker
							value={compareAgainst}
							options={[
								{ value: COMPARE_PREVIOUS, label: 'The version before this one' },
								{ value: COMPARE_CURRENT, label: 'The menu as it is now' },
								...others.map((v, i) => ({
									value: v._id,
									label:
										versionName(
											v,
											scope === ALL_MENUS ? this.changedMenus(v) : scopedSummaries(v, scope),
											v._id === earliestId
										) ||
										describeWhen(v.created_at),
									detail: `${describeWhen(v.created_at)} · ${describeActor(v)}`,
									startsGroup: i === 0,
								})),
							]}
							onChange={(compareAgainst) => this.setState({ compareAgainst }, this.loadDiff)}
						/>
					</div>

					{compareAgainst === COMPARE_PREVIOUS && !this.previousVersion(comparing) ? (
						<div style={{ padding: 20, textAlign: 'center', color: '#666' }}>
							<p>This is the earliest version kept, so there is no version before it to compare with.</p>
						</div>
					) : (
						this.renderDiffBody(diff, diffLoading, diffError)
					)}

					<div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
						<Button full onClick={() => this.setState({ comparing: null, diff: null })}>
							Close
						</Button>
						{this.copyAvailable() && (
							<Button full disabled={restoring} onClick={() => this.askCopy(comparing)}>
								Add as a new menu
							</Button>
						)}
						{this.canRestore && (
							<Button
								full
								color="primary"
								disabled={restoring}
								onClick={() => {
									this.setState({ comparing: null, diff: null });
									this.askConfirm(comparing);
								}}
							>
								Restore this version
							</Button>
						)}
					</div>
				</ModalContent>
			</Modal>
		);
	}

	renderDiffBody(
		diff: T.API.MenuVersionDiffResponse | null,
		loading: boolean,
		error: string
	) {
		if (loading) {
			return (
				<div style={{ padding: 30, textAlign: 'center' }}>
					<RotateLoader size={3} color="#505050" />
				</div>
			);
		}

		if (error) {
			return (
				<div style={{ padding: 20, textAlign: 'center' }}>
					<p style={{ marginBottom: 12 }}>{error}</p>
					<Button size="xs" onClick={this.loadDiff}>
						Try again
					</Button>
				</div>
			);
		}

		if (!diff) return null;

		if (diff.summary.added + diff.summary.removed + diff.summary.modified === 0) {
			return (
				<div style={{ padding: 20, textAlign: 'center', color: '#666' }}>
					<p>Nothing has changed between these two.</p>
				</div>
			);
		}

		return (
			<div>
				<div
					style={{
						fontSize: 12,
						color: '#666',
						background: '#f7f7f7',
						borderRadius: 4,
						padding: '7px 10px',
						marginBottom: 12,
					}}
				>
					{describeSummary(diff.summary)}
				</div>
				<div
					style={{
						maxHeight: 360,
						overflowY: 'auto',
						border: '1px solid #eee',
						borderRadius: 4,
						padding: '10px 12px',
					}}
				>
					{renderChangeGroup('Menus', diff.menus)}
					{renderChangeGroup('Modifiers', diff.option_sets)}
					{renderChangeGroup('Item tags', diff.dish_tags)}
				</div>
			</div>
		);
	}

	renderCopyDialog() {
		const { copyingFrom, copyName, copySubmitting } = this.state;
		if (!copyingFrom) return null;

		const name = copyName.trim();

		return (
			<Modal active width={460} close={() => this.setState({ copyingFrom: null })}>
				<ModalTitle className="round-top-sm">
					<h3 className="text-center flex-grow">Add as a new menu</h3>
				</ModalTitle>
				<ModalContent paddingtb={24} paddinglr={24}>
					<p style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>
						Adds this menu as it was on <strong>{describeWhen(copyingFrom.created_at)}</strong>.
						Your current menus are not changed.
					</p>

					<label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
						Menu name
					</label>
					<input
						value={copyName}
						maxLength={100}
						disabled={copySubmitting}
						autoFocus
						onChange={(e: any) => this.setState({ copyName: e.target.value })}
						style={{
							width: '100%',
							border: '1px solid #e0e0e0',
							borderRadius: 4,
							padding: '9px 10px',
							fontSize: 14,
							fontFamily: 'inherit',
							marginBottom: 20,
						}}
					/>

					<div style={{ display: 'flex', gap: 10 }}>
						<Button
							full
							disabled={copySubmitting}
							onClick={() => this.setState({ copyingFrom: null })}
						>
							Cancel
						</Button>
						<Button
							full
							color="primary"
							disabled={name.length === 0 || copySubmitting}
							onClick={() => this.copyMenu(copyingFrom, name)}
						>
							{copySubmitting ? 'Adding…' : 'Add'}
						</Button>
					</div>
				</ModalContent>
			</Modal>
		);
	}

	renderConfirm() {
		const { confirming, scope, confirmDiff } = this.state;
		if (!confirming) return null;

		const wholeStore = scope === ALL_MENUS;
		// Restoring a version that matches the live menu writes nothing and
		// looks broken. The preview already knows; use it.
		const nothingToRestore =
			!!confirmDiff && confirmDiff.summary.added + confirmDiff.summary.removed + confirmDiff.summary.modified === 0;

		return (
			<Modal active width={420} close={() => this.setState({ confirming: null, confirmDiff: null })}>
				<ModalTitle className="round-top-sm">
					<h3 className="text-center flex-grow">Restore this version?</h3>
				</ModalTitle>
				<ModalContent paddingtb={24} paddinglr={24}>
					<p style={{ marginBottom: 8 }}>
						{wholeStore
							? 'This will replace every menu in the store.'
							: `This will replace the "${this.scopeName()}" menu.`}
					</p>
					<p style={{ fontSize: 13, color: '#666', marginBottom: 14 }}>
						Restoring the version from <strong>{describeWhen(confirming.created_at)}</strong> by{' '}
						{describeActor(confirming)}. Your current menu is saved first, so this can be undone.
					</p>

					<div
						style={{
							background: '#f7f7f7',
							borderRadius: 4,
							padding: '10px 12px',
							marginBottom: 20,
							fontSize: 13,
						}}
					>
						{this.state.confirmDiffLoading ? (
							<span style={{ color: '#8a8a8a' }}>Working out what this will change…</span>
						) : this.state.confirmDiff ? (
							<div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
								<span>{describeDiffSummary(this.state.confirmDiff.summary)}</span>
								{describeDiffSummary(this.state.confirmDiff.summary) !== 'Nothing will change' && (
									<Button
										size="xs"
										onClick={() => {
											this.setState({ confirming: null });
											this.compare(confirming, COMPARE_CURRENT);
										}}
									>
										See details
									</Button>
								)}
							</div>
						) : (
							<span style={{ color: '#8a8a8a' }}>
								Could not work out what this will change. Restoring is still safe to try.
							</span>
						)}
					</div>
					<label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
						Why are you restoring? <span style={{ fontWeight: 400, color: '#8a8a8a' }}>(optional)</span>
					</label>
					<textarea
						value={this.state.confirmReason}
						maxLength={500}
						rows={2}
						placeholder="e.g. prices were wrong after the import"
						onChange={(e: any) => this.setState({ confirmReason: e.target.value })}
						style={{
							width: '100%',
							border: '1px solid #e0e0e0',
							borderRadius: 4,
							padding: '8px 10px',
							fontSize: 13,
							fontFamily: 'inherit',
							resize: 'vertical',
							marginBottom: 20,
						}}
					/>

					<div style={{ display: 'flex', gap: 10 }}>
						<Button full onClick={() => this.setState({ confirming: null, confirmDiff: null })}>
							Cancel
						</Button>
						<Button
							full
							color="primary"
							disabled={nothingToRestore}
							onClick={() => this.restore(confirming)}
						>
							{nothingToRestore ? 'Nothing to restore' : 'Restore'}
						</Button>
					</div>
				</ModalContent>
			</Modal>
		);
	}

	render() {
		const { hideTrigger } = this.props;
		const { active } = this.state;

		return (
			// Inline, and sized like the buttons it sits with: the toolbar it is
			// mounted in lays its children out inline, so a block wrapper here
			// breaks the row in two.
			<div className="inline-block">
				{!hideTrigger && (
					<Button size="xxs" color="white" onClick={this.open}>
						Version history
					</Button>
				)}

				<Modal active={active} width={680} close={this.close}>
					<ModalTitle className="round-top-sm">
						<h3 className="text-center flex-grow">Menu version history</h3>
					</ModalTitle>
					<ModalContent paddingtb={24} paddinglr={24}>
						<p style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>
							{this.canRestore
								? 'Restore your menu to an earlier version. Versions are saved automatically.'
								: 'View and compare earlier versions. Restoring requires the Menus - Restoration permission.'}
						</p>
						{this.renderScopeSelect()}
						{this.renderList()}
					</ModalContent>
				</Modal>

				{this.renderCompare()}
				{this.renderConfirm()}
				{this.renderCopyDialog()}
			</div>
		);
	}
}
